import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Form, Select, Radio, DatePicker, Input, Spin, Tag, message, Tooltip } from 'antd';
import dayjs from 'dayjs';
import adminProductService from '../../services/adminProductService';
import { TYPE_LABEL, CYCLE_LABEL } from './adminProductMeta';

/**
 * 后台替用户开通：产品线 → 套餐 → 周期 → 开通参数 → 扣余额 / 赠送（可指定到期）。
 * 后端走客户同一个下单入口（OrderService）+ 付款结算 → 开通队列。
 */
const AdminProvisionModal = ({ open, userId, types = [], onClose, onDone }) => {
    const [form] = Form.useForm();
    const [opts, setOpts] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const type = Form.useWatch('type', form);
    const productId = Form.useWatch('product_id', form);
    const mode = Form.useWatch('mode', form);
    const resourceId = Form.useWatch('resource_id', form);

    useEffect(() => { if (open) { form.resetFields(); setOpts(null); } }, [open, form]);

    useEffect(() => {
        if (!open || !type) return;
        let alive = true;
        setLoading(true);
        setOpts(null);
        form.setFieldsValue({ product_id: undefined, billing_cycle: undefined, resource_id: undefined, region: undefined });
        adminProductService.options(userId, type).then((r) => {
            if (!alive) return;
            if (r?.success) setOpts(r.data); else message.error(r?.error || '加载失败');
        }).finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [open, type, userId, form]);

    const plan = useMemo(() => (opts?.plans || []).find((p) => p.id === productId), [opts, productId]);
    const diskOrg = type === 'disk' && ['org_pool', 'org_seat'].includes(plan?.features?.disk_mode);
    const newMailDomain = type === 'yeapmail' && !resourceId;

    const submit = async () => {
        const v = await form.validateFields();
        const config = {};
        ['domain', 'password', 'name', 'stream_type', 'org_name', 'image', 'label'].forEach((k) => { if (v[k] !== undefined && v[k] !== '') config[k === 'stream_type' ? 'type' : k] = v[k]; });
        setSaving(true);
        try {
            const r = await adminProductService.provision({
                user_id: userId,
                product_id: v.product_id,
                billing_cycle: v.billing_cycle,
                mode: opts?.owner ? 'grant' : v.mode,
                expiry_date: v.mode === 'grant' && v.expiry_date ? v.expiry_date.format('YYYY-MM-DD HH:mm:ss') : undefined,
                resource_id: v.resource_id,
                region: v.region,
                config,
            });
            if (!r?.success) { message.error(r?.error || '开通失败'); return; }
            message.success(`已下单 #${r.subscription_id}，开通队列处理中`);
            onDone && onDone();
            onClose && onClose();
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal title="开通产品" open={open} onCancel={onClose} onOk={submit} confirmLoading={saving} okText="开通" cancelText="取消" destroyOnClose>
            <Spin spinning={loading}>
                <Form form={form} layout="vertical" initialValues={{ mode: 'grant', stream_type: 'transactional' }}>
                    <Form.Item name="type" label="产品" rules={[{ required: true }]}>
                        <Select options={types.map((t) => ({ value: t, label: TYPE_LABEL[t] || t }))} />
                    </Form.Item>
                    {opts && (
                        <>
                            <Form.Item name="product_id" label="套餐" rules={[{ required: true, message: '请选择套餐' }]}>
                                <Select
                                    options={opts.plans.map((p) => ({ value: p.id, label: p.name }))}
                                    onChange={(id) => {
                                        const p = opts.plans.find((x) => x.id === id);
                                        form.setFieldsValue({ billing_cycle: p?.pricing?.[0]?.billing_cycle });
                                    }}
                                />
                            </Form.Item>
                            <Form.Item name="billing_cycle" label="计费周期" rules={[{ required: true }]}>
                                <Select options={(plan?.pricing || []).map((p) => ({ value: p.billing_cycle, label: `${CYCLE_LABEL[p.billing_cycle] || p.billing_cycle} · ${p.price} ${p.currency || ''}` }))} />
                            </Form.Item>

                            {['cdn', 'dns', 'yeapmail'].includes(type) && (
                                <Form.Item name="resource_id" label={type === 'dns' ? '区域' : '域名'}
                                    rules={type === 'yeapmail' ? [] : [{ required: true, message: '请选择' }]}
                                    tooltip={type === 'yeapmail' ? '留空 = 新开一个邮箱域名' : '只列用户自己的；已有付费套餐的请改套餐'}>
                                    <Select allowClear showSearch optionFilterProp="label"
                                        options={opts.resources.map((r) => ({ value: r.id, label: r.name, disabled: r.has_paid_plan }))} />
                                </Form.Item>
                            )}
                            {(['host_shared', 'container'].includes(type) || newMailDomain) && (
                                <Form.Item name="region" label="地区" rules={[{ required: true, message: '请选择地区' }]}>
                                    <Select options={(opts.regions || []).map((r) => ({ value: r.id, label: r.name || r.id }))} />
                                </Form.Item>
                            )}
                            {(type === 'host_shared' || newMailDomain) && (
                                <Form.Item name="domain" label="域名" rules={[{ required: true }]}><Input placeholder="example.com" /></Form.Item>
                            )}
                            {type === 'host_shared' && (
                                <Form.Item name="password" label="FTP 密码" rules={[{ required: true, min: 8 }]}><Input.Password /></Form.Item>
                            )}
                            {type === 'tm' && (
                                <>
                                    <Form.Item name="name" label="发信流名称" rules={[{ required: true }]}><Input maxLength={64} /></Form.Item>
                                    <Form.Item name="stream_type" label="类型">
                                        <Radio.Group options={[{ value: 'transactional', label: '事务' }, { value: 'marketing', label: '营销' }]} />
                                    </Form.Item>
                                </>
                            )}
                            {diskOrg && (
                                <Form.Item name="org_name" label="组织名称" rules={[{ required: true, min: 2, max: 60 }]}><Input /></Form.Item>
                            )}
                            {type === 'container' && (
                                <>
                                    <Form.Item name="image" label="镜像" rules={[{ required: true }]}><Input placeholder="nginx:latest" /></Form.Item>
                                    <Form.Item name="label" label="名称"><Input /></Form.Item>
                                </>
                            )}

                            <Form.Item name="mode" label="费用">
                                {opts.owner ? <Tag color="gold">业主账号：赠送，到期 2030-12-31</Tag> : (
                                    <Radio.Group>
                                        <Radio value="grant">赠送</Radio>
                                        <Radio value="charge">扣余额</Radio>
                                    </Radio.Group>
                                )}
                            </Form.Item>
                            {!opts.owner && mode === 'grant' && (
                                <Form.Item name="expiry_date" label={<Tooltip title="留空 = 按计费周期">到期时间</Tooltip>}>
                                    <DatePicker showTime format="YYYY-MM-DD HH:mm" disabledDate={(d) => d && d.isBefore(dayjs(), 'day')} />
                                </Form.Item>
                            )}
                        </>
                    )}
                </Form>
            </Spin>
        </Modal>
    );
};

export default AdminProvisionModal;
